import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const STORIES_PER_PAGE = 12;

// Helper function to format date safely
const formatDate = (dateString) => {
  if (!dateString) return 'Recent';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Recent';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (error) {
    return 'Recent';
  }
};

// Build slug-based URL for a story (canonical, SEO-friendly)
const storyHref = (s) => {
  if (s.slug) return `/story/${encodeURIComponent(s.slug)}`;
  if (s.story_id) return `/story/${encodeURIComponent(s.story_id)}`;
  return '#';
};

// Build author URL
const authorHref = (author) => `/author/${encodeURIComponent(author || 'Anonymous')}`;

function StoryList({ stories, loading, onViewStory, onViewAuthor, user, login, type = 'default', onDeleteStory }) {
  const isAdmin = user?.email === 'sandip.kumar.kol@gmail.com';
  const [visibleCount, setVisibleCount] = useState(STORIES_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);

  // Reset pagination when stories change
  useEffect(() => {
    setVisibleCount(STORIES_PER_PAGE);
    setCurrentPage(1);
  }, [stories]);

  const handleDelete = async (e, story) => {
    e.preventDefault();
    e.stopPropagation();
    const storyId = story.story_id || story.id;
    if (!window.confirm(`Are you sure you want to delete "${story.title || 'Untitled'}"? This cannot be undone.`)) {
      return;
    }
    try {
      const response = await axios.delete(`${API_URL}/admin/stories/${storyId}`, { withCredentials: true });
      if (response.data.success) {
        if (onDeleteStory) onDeleteStory(storyId);
      } else {
        alert('Failed to delete story: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        if (onDeleteStory) onDeleteStory(storyId);
        return;
      }
      console.error('Error deleting story:', error);
      alert('Failed to delete story: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + STORIES_PER_PAGE);
    setCurrentPage(prev => prev + 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setVisibleCount(page * STORIES_PER_PAGE);
    setTimeout(() => {
      const storyList = document.querySelector('.story-list');
      if (storyList) {
        storyList.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  if (loading) {
    return <div className="loading">Loading stories...</div>;
  }

  if (!stories || stories.length === 0) {
    return <div className="no-stories">No stories found. Be the first to write one!</div>;
  }

  const totalStories = stories.length;
  const visibleStories = stories.slice(0, visibleCount);
  const hasMore = visibleCount < totalStories;
  const totalPages = Math.ceil(totalStories / STORIES_PER_PAGE);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className={`story-list ${type}`}>
      <div className="story-list-header">
        <p className="story-count">
          Showing <strong>{visibleStories.length}</strong> of <strong>{totalStories}</strong> {totalStories === 1 ? 'story' : 'stories'}
        </p>
      </div>

      <div className="stories-grid">
        {visibleStories.map(story => {
          // Resolve the cover image src — prefer base64 (LLM illustrations), fall back to image_path.
          const rawCover = story.cover_image || story.image_path || null;
          const isSafeSrc = (s) => s && typeof s === 'string' && (
            s.startsWith('data:image/') || /^https?:\/\//.test(s) || s.startsWith('/uploads/') || s.startsWith('/images/')
          );
          const coverSrc = isSafeSrc(rawCover) ? rawCover : null;
          return (
          <article key={story.story_id || story.id} className="story-card">
            {coverSrc && (
              <Link
                to={storyHref(story)}
                className="story-card-image-link"
                title={`Read ${story.title || 'Untitled'}`}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                  if (onViewStory) {
                    e.preventDefault();
                    onViewStory(story.story_id || story.id);
                  }
                }}
              >
                <img
                  src={coverSrc}
                  alt={story.title || 'Story cover'}
                  loading="lazy"
                  decoding="async"
                  className="story-card-image"
                />
              </Link>
            )}
            <Link
              to={storyHref(story)}
              className="story-card-link"
              title={`Read ${story.title || 'Untitled'} by ${story.author || 'Anonymous'} online`}
              onClick={(e) => {
                // Allow modifier keys to open in new tab natively
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                if (onViewStory) {
                  e.preventDefault();
                  onViewStory(story.story_id || story.id);
                }
              }}
            >
              <div className="story-card-content">
                <div className="story-card-header-row">
                  <h3>{story.title || 'Untitled'}</h3>
                  {isAdmin && (
                    <button
                      className="admin-delete-btn story-card-delete"
                      onClick={(e) => handleDelete(e, story)}
                      title="Delete this story"
                    >
                      🗑️
                    </button>
                  )}
                </div>
                <p className="story-excerpt">
                  {story.meta_description || story.content_text?.substring(0, 120) || story.plain_text?.substring(0, 120) || 'No preview available'}...
                </p>
                <div className="story-card-meta">
                  {onViewAuthor && story.author ? (
                    <Link
                      to={authorHref(story.author)}
                      className="story-card-author"
                      title={`See all stories by ${story.author}`}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                        e.preventDefault();
                        e.stopPropagation();
                        onViewAuthor(story.author);
                      }}
                    >
                      ✍️ {story.author}
                    </Link>
                  ) : (
                    <span>✍️ {story.author || 'Anonymous'}</span>
                  )}
                  <span>❤️ {story.like_count || 0}</span>
                  <span>💬 {story.comment_count || 0}</span>
                  <span>📅 {formatDate(story.created_at || story.publish_date)}</span>
                </div>
                {story.genre && (
                  <div className="story-genre">{story.genre}</div>
                )}
              </div>
            </Link>
          </article>
          );
        })}
      </div>

      {hasMore && (
        <div className="load-more-container">
          <button onClick={handleLoadMore} className="load-more-btn">
            📖 Load More Stories ({totalStories - visibleCount} remaining)
          </button>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination-container">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn pagination-prev"
          >
            ← Previous
          </button>

          <div className="pagination-numbers">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`pagination-btn pagination-number ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn pagination-next"
          >
            Next →
          </button>
        </div>
      )}

      {!hasMore && totalStories > STORIES_PER_PAGE && (
        <div className="end-of-list">
          <p>🎉 You've reached the end! Showing all {totalStories} stories.</p>
        </div>
      )}
    </div>
  );
}

export default StoryList;